import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Package, 
  Truck, 
  Headphones, 
  Printer, 
  CheckCircle, 
  Clock, 
  MapPin, 
  Phone, 
  Mail, 
  ArrowRight,
  Star,
  Shield,
  Zap
} from 'lucide-react';
import SEO from '../components/SEO';

const Services: React.FC = () => {
  const mainServices = [
    {
      icon: <Package className="h-12 w-12 text-primary-600" />,
      title: "Fabricación Personalizada",
      description: "Bolsas de plástico LDPE completamente personalizadas según tus necesidades específicas.",
      features: [
        "Todos los anchos y largos (máximo 1.20 metros)",
        "Todos los colores disponibles",
        "Grosor hasta 200 micrones",
        "Material LDPE de primera calidad",
        "Cantidades desde 1,000 unidades"
      ],
      benefits: [
        "Calidad garantizada",
        "Cumplimiento de especificaciones exactas",
        "Material resistente y duradero"
      ]
    },
    {
      icon: <Truck className="h-12 w-12 text-primary-600" />,
      title: "Servicio de Entrega",
      description: "Entrega a domicilio en San Juan y alrededores para mayor comodidad.",
      image: "/images/servicio-entrega.png",
      features: [
        "Entrega a domicilio en San Juan",
        "Cobertura en zonas aledañas",
        "Tiempo de entrega: 7 a 15 días",
        "Seguimiento de pedidos",
        "Entrega programada"
      ],
      benefits: [
        "Comodidad total",
        "Ahorro de tiempo",
        "Entrega segura y confiable"
      ]
    },
    {
      icon: <Printer className="h-12 w-12 text-primary-600" />,
      title: "Impresión Terciarizada",
      description: "Servicio de impresión y personalización de logos y textos en tus bolsas.",
      features: [
        "Impresión de logos corporativos",
        "Textos personalizados",
        "Diseños a medida",
        "Múltiples colores de impresión",
        "Asesoramiento en diseño"
      ],
      benefits: [
        "Identidad de marca",
        "Profesionalismo",
        "Diferenciación en el mercado"
      ]
    }
  ];

  const additionalServices = [
    {
      icon: <Headphones className="h-8 w-8 text-primary-600" />,
      title: "Asesoramiento Técnico",
      description: "Recomendaciones personalizadas para cada necesidad específica de tu negocio."
    },
    {
      icon: <Shield className="h-8 w-8 text-primary-600" />,
      title: "Garantía de Calidad",
      description: "desde el año 2013 garantizamos la mejor calidad en cada producto."
    },
    {
      icon: <Zap className="h-8 w-8 text-primary-600" />,
      title: "Entrega Rápida",
      description: "Tiempos de entrega optimizados de 7 a 15 días según el pedido."
    },
    {
      icon: <Star className="h-8 w-8 text-primary-600" />,
      title: "Atención Personalizada",
      description: "Servicio al cliente dedicado para cada proyecto y cliente."
    }
  ];

  const processSteps = [
    {
      step: "01",
      title: "Consulta Inicial",
      description: "Contactanos con tus requerimientos específicos o solicita una cotización personalizada."
    },
    {
      step: "02",
      title: "Propuesta Técnica",
      description: "Te enviamos una propuesta detallada con especificaciones, precios y tiempos de entrega."
    },
    {
      step: "03",
      title: "Aprobación y Producción",
      description: "Una vez aprobada la propuesta, iniciamos la producción con los más altos estándares de calidad."
    },
    {
      step: "04",
      title: "Entrega y Seguimiento",
      description: "Entregamos tu pedido en tiempo y forma, con seguimiento completo del proceso."
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO 
        title="Servicios"
        description="Servicios especializados de Plásticos Polcen: entrega a domicilio, asesoramiento técnico, impresión terciarizada. Soluciones completas para tu negocio."
        keywords="servicios plásticos, entrega domicilio, asesoramiento técnico, impresión personalizada, logística, soporte técnico"
      />
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-primary-600 to-primary-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Nuestros Servicios
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-primary-100">
              Soluciones completas para todas tus necesidades en bolsas de plástico
            </p>
            <p className="text-lg mb-10 text-primary-200 max-w-3xl mx-auto">
              Desde el año 2013, ofrecemos servicios integrales desde la fabricación 
              personalizada hasta la entrega a domicilio, garantizando la mejor calidad y atención.
            </p>
          </div>
        </div>
      </section>

      {/* Servicios Principales */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Servicios Principales
            </h2>
            <p className="text-lg text-gray-600">
              Nuestros servicios especializados para satisfacer todas tus necesidades
            </p>
          </div>

          <div className="space-y-16">
            {mainServices.map((service, index) => (
              <div key={index} className={`flex flex-col ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'} items-center gap-12`}>
                <div className="flex-1">
                  <div className="flex items-center mb-6">
                    {service.icon}
                    <h3 className="text-2xl font-bold text-gray-900 ml-4">
                      {service.title}
                    </h3>
                  </div>
                  <p className="text-lg text-gray-600 mb-6">
                    {service.description}
                  </p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-3">Características:</h4>
                      <ul className="space-y-2">
                        {service.features.map((feature, idx) => (
                          <li key={idx} className="flex items-start">
                            <CheckCircle className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                            <span className="text-gray-600">{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-3">Beneficios:</h4>
                      <ul className="space-y-2">
                        {service.benefits.map((benefit, idx) => (
                          <li key={idx} className="flex items-start">
                            <Star className="h-5 w-5 text-yellow-500 mr-2 mt-0.5 flex-shrink-0" />
                            <span className="text-gray-600">{benefit}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
                
                <div className="flex-1">
                  <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                    <img
                      src={
                        index === 0
                          ? "/images/fabricacion-personalizada.png"
                          : index === 1
                            ? "/images/servicio-entrega.png"
                            : "/images/impresion-tercerizada.png"
                      }
                      alt={service.title}
                      className="w-full h-64 object-cover"
                      loading="lazy"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Servicios Adicionales */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Servicios Adicionales
            </h2>
            <p className="text-lg text-gray-600">
              Valor agregado que nos distingue en el mercado
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {additionalServices.map((service, index) => (
              <div key={index} className="text-center p-6 bg-gray-50 rounded-lg hover:shadow-md transition-shadow">
                <div className="flex justify-center mb-4">
                  {service.icon}
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">
                  {service.title}
                </h3>
                <p className="text-gray-600">
                  {service.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Proceso de Trabajo */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Nuestro Proceso de Trabajo
            </h2>
            <p className="text-lg text-gray-600">
              Cómo trabajamos contigo para lograr los mejores resultados
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {processSteps.map((step, index) => (
              <div key={index} className="text-center">
                <div className="bg-primary-600 text-white rounded-full w-16 h-16 flex items-center justify-center text-xl font-bold mx-auto mb-4">
                  {step.step}
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">
                  {step.title}
                </h3>
                <p className="text-gray-600">
                  {step.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Especificaciones Técnicas */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Especificaciones Técnicas
            </h2>
            <p className="text-lg text-gray-600">
              Capacidades y limitaciones de nuestros servicios
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Dimensiones</h3>
              <ul className="space-y-2 text-gray-600">
                <li>• Ancho máximo: 1.20 metros</li>
                <li>• Largo máximo: 1.20 metros</li>
                <li>• Grosor: hasta 200 micrones</li>
                <li>• Medidas personalizadas</li>
              </ul>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Material</h3>
              <ul className="space-y-2 text-gray-600">
                <li>• Material: LDPE (Polietileno de baja densidad)</li>
                <li>• Calidad: Primera calidad</li>
                <li>• Resistencia: Alta durabilidad</li>
                <li>• Colores: Amplia gama disponible</li>
              </ul>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Producción</h3>
              <ul className="space-y-2 text-gray-600">
                <li>• Cantidad mínima: 1,000 unidades</li>
                <li>• Tiempo de entrega: 7-15 días</li>
                <li>• Impresión: Terciarizada</li>
                <li>• Control de calidad: Riguroso</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-primary-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4">
            ¿Listo para comenzar tu proyecto?
          </h2>
          <p className="text-xl text-primary-100 mb-8">
            Contáctanos y te ayudaremos a encontrar la solución perfecta para tus necesidades
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contacto"
              className="inline-flex items-center px-8 py-3 bg-white text-primary-600 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
            >
              Solicitar Cotización
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
            <a
              href="tel:+542644114422"
              className="inline-flex items-center px-8 py-3 border-2 border-white text-white rounded-lg font-semibold hover:bg-white hover:text-primary-600 transition-colors"
            >
              <Phone className="mr-2 h-5 w-5" />
              Llamar Ahora
            </a>
          </div>

          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-8 text-primary-100">
            <div className="flex items-center">
              <Mail className="h-5 w-5 mr-2" />
              <span>plasticospolcen@gmail.com</span>
            </div>
            <div className="flex items-center">
              <MapPin className="h-5 w-5 mr-2" />
              <span>Santiago de Liniers 1239, Rivadavia, San Juan</span>
            </div>
            <div className="flex items-center">
              <Clock className="h-5 w-5 mr-2" />
              <span>Lunes a Viernes 08:00 - 17:00</span>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;
